import { useEffect, useRef, useState } from 'react';

const areas = [
  {
    priority: 'Priority 01',
    badge: 'Launch Priority',
    badgeColor: 'bg-amber-600 text-white',
    icon: 'ri-fire-line',
    title: 'Performance-Based Hotel Management',
    desc: "The market's only incentive-aligned Hotel Management Service. We tie our fees to RevPAR, occupancy, and NOI benchmarks — and we put it in writing. Near-zero competition in paid search. First-mover positioning you won't find anywhere else.",
    featured: true,
  },
  {
    priority: 'Priority 02',
    badge: 'Core Service',
    badgeColor: 'bg-gray-100 text-gray-600',
    icon: 'ri-hotel-line',
    title: 'Hotel Management Services',
    desc: 'Full-service third-party Hotel Management Service for independent, boutique, and select-service properties. We handle operations, staffing, revenue strategy, and brand compliance so you can focus on growth.',
    featured: false,
  },
  {
    priority: 'Priority 03',
    badge: 'High Demand',
    badgeColor: 'bg-orange-100 text-orange-700',
    icon: 'ri-file-text-line',
    title: 'Contract & HMA Advisory',
    desc: 'Hotel Management Agreement negotiation, operator selection, HMA review, and contract restructuring. With PIPs expiring across 16,000+ branded properties, the timing has never been better.',
    featured: false,
  },
  {
    priority: '04',
    badge: 'Core Service',
    badgeColor: 'bg-gray-100 text-gray-600',
    icon: 'ri-bar-chart-box-line',
    title: 'Asset Optimization',
    desc: 'NOI improvement, RevPAR benchmarking, cap rate optimization, and portfolio management through our Hotel Revenue Management Service for hotel owners and institutional investors. Every engagement targets measurable asset value growth.',
    featured: false,
  },
  {
    priority: '05',
    badge: 'Core Service',
    badgeColor: 'bg-gray-100 text-gray-600',
    icon: 'ri-settings-3-line',
    title: 'Operational Excellence Programs',
    desc: 'Labor optimization, F&B efficiency, housekeeping systems, and cost reduction consulting. With hotel labor costs 15–20% above pre-pandemic levels, operational precision is no longer optional.',
    featured: false,
  },
  {
    priority: '06',
    badge: 'Core Service',
    badgeColor: 'bg-gray-100 text-gray-600',
    icon: 'ri-compass-3-line',
    title: 'Strategic Management Solutions',
    desc: 'Hotel turnaround management, market positioning, brand strategy, feasibility studies, and growth consulting through our Hotel Brand Management Service for developers and ownership groups entering or expanding in the hospitality sector.',
    featured: false,
  },
];

export default function PracticeAreas() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-gray-50 py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-2xl mb-16">
          <p className="text-xs font-light text-amber-600 uppercase tracking-widest mb-4" style={{ letterSpacing: '0.25em' }}>
            Six Practice Areas
          </p>
          <h2
            className="text-4xl lg:text-5xl font-extralight text-gray-900 leading-tight"
          >
            Everything Your Hotel Needs. Under One Roof.
          </h2>
        </div>

        {/* Grid */}
        <div ref={ref} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {areas.map((area, i) => (
            <div
              key={i}
              className={`relative rounded-2xl p-8 flex flex-col transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              } ${
                area.featured
                  ? 'bg-gray-950 text-white border-2 border-amber-600'
                  : 'bg-white border border-gray-100 hover:border-amber-200 hover:bg-amber-50/20'
              }`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              {/* Priority + Badge */}
              <div className="flex items-center justify-between mb-6">
                <span className={`text-xs font-light uppercase tracking-widest ${area.featured ? 'text-amber-400' : 'text-gray-400'}`} style={{ letterSpacing: '0.2em' }}>
                  {area.priority}
                </span>
                <span className={`text-xs font-light px-3 py-1 rounded-full ${area.badgeColor}`}>
                  {area.badge}
                </span>
              </div>

              {/* Icon */}
              <div className={`w-11 h-11 flex items-center justify-center rounded-xl mb-5 ${area.featured ? 'bg-amber-600/20' : 'bg-amber-50'}`}>
                <i className={`${area.icon} text-2xl ${area.featured ? 'text-amber-400' : 'text-amber-600'}`}></i>
              </div>

              {/* Content */}
              <h3
                className={`text-lg font-light mb-3 leading-snug ${area.featured ? 'text-white' : 'text-gray-900'}`}
              >
                {area.title}
              </h3>
              <p className={`text-sm font-light leading-relaxed flex-1 ${area.featured ? 'text-gray-400' : 'text-gray-500'}`}>
                {area.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
